import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Globe, Clock, DollarSign, Loader2 } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export default function OrganizationSettings() {
  const { currentOrg, isLoading: tenantLoading } = useTenant();
  const [saving, setSaving] = useState(false);

  // Form state
  const [orgName, setOrgName] = useState("");
  const [orgType, setOrgType] = useState("sfo");
  const [baseCcy, setBaseCcy] = useState("USD");
  const [timezone, setTimezone] = useState("Asia/Singapore");
  const [jurisdiction, setJurisdiction] = useState("sg");
  const [reportingCcy, setReportingCcy] = useState("USD");
  const [dateFormat, setDateFormat] = useState("mdy");
  const [numberFormat, setNumberFormat] = useState("us");
  const [regFramework, setRegFramework] = useState("mas");
  const [fiscalYearEnd, setFiscalYearEnd] = useState("dec");

  // Load current org data into form
  useEffect(() => {
    if (currentOrg) {
      setOrgName(currentOrg.name || "");
      setOrgType(currentOrg.org_type || "sfo");
      setBaseCcy(currentOrg.base_ccy || "USD");
      setTimezone(currentOrg.tz || "Asia/Singapore");
      setJurisdiction(currentOrg.jurisdiction || "sg");
      setReportingCcy(currentOrg.reporting_ccy || "USD");
      setDateFormat(currentOrg.date_format || "mdy");
      setNumberFormat(currentOrg.number_format || "us");
      setRegFramework(currentOrg.reg_framework || "mas");
      setFiscalYearEnd(currentOrg.fiscal_year_end || "dec");
    }
  }, [currentOrg]);

  const handleSave = async () => {
    if (!currentOrg?.id) {
      toast.error("No organization found");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('orgs')
        .update({
          name: orgName,
          org_type: orgType,
          base_ccy: baseCcy,
          tz: timezone,
          jurisdiction,
          reporting_ccy: reportingCcy,
          date_format: dateFormat,
          number_format: numberFormat,
          reg_framework: regFramework,
          fiscal_year_end: fiscalYearEnd,
        })
        .eq('id', currentOrg.id);

      if (error) throw error;

      toast.success("Organization settings saved");
      // Refresh the page to update the sidebar
      window.location.reload();
    } catch (err) {
      toast.error("Failed to save", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (tenantLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-4 px-6 py-6">

      <div>
        <h1 className="text-2xl font-bold tracking-tight">Organization</h1>
        <p className="text-muted-foreground">
          Configure organization details, currency, and timezone
        </p>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <CardTitle>Organization Details</CardTitle>
            </div>
            <CardDescription>
              Legal name and organizational information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="org-name">Organization Name</Label>
              <Input
                id="org-name"
                placeholder="Family Office Name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="org-type">Organization Type</Label>
                <Select value={orgType} onValueChange={setOrgType}>
                  <SelectTrigger id="org-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sfo">Single Family Office</SelectItem>
                    <SelectItem value="mfo">Multi Family Office</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="jurisdiction">Jurisdiction</Label>
                <Select value={jurisdiction} onValueChange={setJurisdiction}>
                  <SelectTrigger id="jurisdiction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="us">United States</SelectItem>
                    <SelectItem value="uk">United Kingdom</SelectItem>
                    <SelectItem value="sg">Singapore</SelectItem>
                    <SelectItem value="ae">UAE</SelectItem>
                    <SelectItem value="ch">Switzerland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              <CardTitle>Currency Settings</CardTitle>
            </div>
            <CardDescription>
              Base and reporting currency configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="base-ccy">Base Currency</Label>
                <Select value={baseCcy} onValueChange={setBaseCcy}>
                  <SelectTrigger id="base-ccy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                    <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Primary currency for valuations and reporting
                </p>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="reporting-ccy">Reporting Currency</Label>
                <Select value={reportingCcy} onValueChange={setReportingCcy}>
                  <SelectTrigger id="reporting-ccy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Currency for client reports and statements
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              <CardTitle>Regional Settings</CardTitle>
            </div>
            <CardDescription>
              Timezone and date formatting
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger id="timezone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern Time (EST/EDT)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time (PST/PDT)</SelectItem>
                  <SelectItem value="UTC">GMT/UTC</SelectItem>
                  <SelectItem value="Europe/London">London (GMT/BST)</SelectItem>
                  <SelectItem value="Europe/Zurich">Central European Time</SelectItem>
                  <SelectItem value="Asia/Singapore">Singapore Time</SelectItem>
                  <SelectItem value="Asia/Hong_Kong">Hong Kong Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="date-format">Date Format</Label>
                <Select value={dateFormat} onValueChange={setDateFormat}>
                  <SelectTrigger id="date-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mdy">MM/DD/YYYY</SelectItem>
                    <SelectItem value="dmy">DD/MM/YYYY</SelectItem>
                    <SelectItem value="ymd">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="number-format">Number Format</Label>
                <Select value={numberFormat} onValueChange={setNumberFormat}>
                  <SelectTrigger id="number-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="us">1,234,567.89</SelectItem>
                    <SelectItem value="eu">1.234.567,89</SelectItem>
                    <SelectItem value="ch">1'234'567.89</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              <CardTitle>Compliance & Regulatory</CardTitle>
            </div>
            <CardDescription>
              Regulatory jurisdiction and compliance requirements
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="reg-framework">Regulatory Framework</Label>
              <Select value={regFramework} onValueChange={setRegFramework}>
                <SelectTrigger id="reg-framework">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sec">SEC (US)</SelectItem>
                  <SelectItem value="fca">FCA (UK)</SelectItem>
                  <SelectItem value="mas">MAS (Singapore)</SelectItem>
                  <SelectItem value="finma">FINMA (Switzerland)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fiscal-year">Fiscal Year End</Label>
              <Select value={fiscalYearEnd} onValueChange={setFiscalYearEnd}>
                <SelectTrigger id="fiscal-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dec">December 31</SelectItem>
                  <SelectItem value="mar">March 31</SelectItem>
                  <SelectItem value="jun">June 30</SelectItem>
                  <SelectItem value="sep">September 30</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline">Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
